'use client';

import type { LinkProps } from '@mui/material/Link';

import { useId } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import Link from '@mui/material/Link';
import { styled, useTheme, useColorScheme } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/global-config';

import { logoClasses } from './classes';

// ----------------------------------------------------------------------

export type LogoProps = LinkProps & {
  isSingle?: boolean;
  disabled?: boolean;
  logoTheme?: 'auto' | 'light' | 'dark';
};

export function Logo({
  sx,
  disabled,
  className,
  href = '/',
  isSingle = true,
  logoTheme = 'auto',
  ...other
}: LogoProps) {
  const theme = useTheme();
  const { mode } = useColorScheme();
  const uniqueId = useId();
  const resolvedMode = mode ?? theme.palette.mode;
  const resolvedLogoTheme = logoTheme === 'auto' ? (resolvedMode === 'dark' ? 'dark' : 'light') : logoTheme;
  const isDarkMode = resolvedLogoTheme === 'dark';

  const fullLogoSrc = `${CONFIG.assetsDir}/logo/vlamsafe-logo.svg`;

  const singleLogo = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 93 124"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={`${uniqueId}-vlamsafe-mark`}
          x1="-5.23"
          y1="-418.24"
          x2="96.49"
          y2="-298"
          gradientTransform="translate(0 -299) scale(1 -1)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#D54045" />
          <stop offset="1" stopColor="#E68B55" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${uniqueId}-vlamsafe-mark)`}
        d="M35.97,21.66c-.56,1.92-1.34,3.97-2.36,6.14H6.14v49.72c0,22.24,18.11,40.34,40.35,40.34s40.35-18.1,40.35-40.34V27.8h-27.68c-.54-2.11-1.24-4.15-2.09-6.14h35.91v55.86c0,25.67-20.82,46.48-46.49,46.48S0,103.19,0,77.52V21.66h35.97ZM36.59,0c.93.85,15.9,14.65,19.1,30.9.02.1.04.21.04.31.37,1.84.56,3.72.56,5.62,0,19.2-26.5,26.97-26.5,50.34v.84c.27,18.24,8.59,23.38,11.76,24.69-.23-.04-.47-.06-.7-.1-8.08-1.26-15.28-5.23-20.61-10.93h-.02c-5.5-5.91-9.03-13.68-9.55-22.26,1.28-14.47,17.61-31.62,25.86-48.22.04-.1.1-.21.15-.31,2.73-5.58,4.55-11.06,4.55-16.39,0-4.13,0-8.8-4.63-14.49ZM75,55.51c.23.29,6.84,9.38,7.26,17.86v4.26c-.1,9.03-3.54,17.26-9.14,23.52-4.22,4.71-9.65,8.29-15.84,10.25-.56.17-1.1.33-1.65.47-.5-9.77,1.43-17.75,8.97-25.73,8.85-9.34,16.64-18.17,10.4-30.63ZM62.51,39.41s5.19,7.81,5.19,15.58c0,7.77-2.6,12.46-11.43,22.84h.02c-8.83,10.4-12.47,17.13-9.86,23.89-5.21-3.64-8.85-17.65,2.07-30.11,10.89-12.46,17.65-19.22,14.02-32.2Z"
      />
    </svg>
  );

  const darkModeFullLogo = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 522.52 124"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={`${uniqueId}-vv-grad`}
          x1="-5.23"
          y1="119.24"
          x2="96.49"
          y2="-1"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#d54045" />
          <stop offset="1" stopColor="#e68b55" />
        </linearGradient>
      </defs>

      <path
        fill={`url(#${uniqueId}-vv-grad)`}
        d="M35.97,21.66c-.56,1.92-1.34,3.97-2.36,6.14H6.14v49.72c0,22.24,18.11,40.34,40.35,40.34s40.35-18.1,40.35-40.34V27.8h-27.68c-.54-2.11-1.24-4.15-2.09-6.14h35.91v55.86c0,25.67-20.82,46.48-46.49,46.48S0,103.19,0,77.52V21.66h35.97ZM36.59,0c.93.85,15.9,14.65,19.1,30.9.02.1.04.21.04.31.37,1.84.56,3.72.56,5.62,0,19.2-26.5,26.97-26.5,50.34v.84c.27,18.24,8.59,23.38,11.76,24.69-.23-.04-.47-.06-.7-.1-8.08-1.26-15.28-5.23-20.61-10.93h-.02c-5.5-5.91-9.03-13.68-9.55-22.26,1.28-14.47,17.61-31.62,25.86-48.22.04-.1.1-.21.15-.31,2.73-5.58,4.55-11.06,4.55-16.39,0-4.13,0-8.8-4.63-14.49ZM75,55.51c.23.29,6.84,9.38,7.26,17.86v4.26c-.1,9.03-3.54,17.26-9.14,23.52-4.22,4.71-9.65,8.29-15.84,10.25-.56.17-1.1.33-1.65.47-.5-9.77,1.43-17.75,8.97-25.73,8.85-9.34,16.64-18.17,10.4-30.63ZM62.51,39.41s5.19,7.81,5.19,15.58c0,7.77-2.6,12.46-11.43,22.84h.02c-8.83,10.4-12.47,17.13-9.86,23.89-5.21-3.64-8.85-17.65,2.07-30.11,10.89-12.46,17.65-19.22,14.02-32.2Z"
      />

      <path
        fill="#FFFFFF"
        d="M352.18,38.01c4.46,0,8.87.81,12.65,2.42,3.78,1.61,6.9,3.78,9.39,6.51l-9.57,9.57c-1.99-2.05-3.99-3.55-6-4.57-2.03-.99-4.26-1.49-6.74-1.49s-4.57.47-6.04,1.45c-1.49.97-2.23,2.34-2.23,4.13,0,1.61.56,2.93,1.67,3.95,1.12,1.01,2.58,1.9,4.42,2.65,1.82.74,3.85,1.49,6.04,2.23,2.19.74,4.4,1.59,6.62,2.56,2.19.97,4.2,2.21,5.99,3.76,1.8,1.55,3.27,3.47,4.42,5.77,1.14,2.29,1.71,5.19,1.71,8.66,0,6.26-2.23,11.18-6.7,14.74-4.46,3.58-10.67,5.35-18.61,5.35h-.02c-5.46,0-10.13-.87-13.99-2.6-3.89-1.74-7.4-4.4-10.56-8l9.49-9.49c2.11,2.42,4.44,4.3,7.03,5.62,2.58,1.34,5.56,2,8.97,2,3.04,0,5.42-.58,7.11-1.72,1.72-1.14,2.56-2.71,2.56-4.69,0-1.74-.54-3.16-1.63-4.28-1.1-1.12-2.56-2.07-4.42-2.89-1.86-.81-3.89-1.57-6.04-2.27-2.17-.7-4.36-1.57-6.55-2.56-2.19-.99-4.22-2.19-6.04-3.64-1.82-1.43-3.31-3.24-4.42-5.48-1.12-2.23-1.68-4.98-1.68-8.27,0-4.09.97-7.58,2.94-10.5,1.94-2.91,4.69-5.12,8.23-6.65,3.54-1.51,7.53-2.27,11.99-2.27ZM499.53,58.45c4.61,0,8.5.97,11.91,2.89,3.41,1.92,6.1,4.59,8.1,8,1.98,3.41,2.98,7.32,2.98,11.72,0,.87-.04,1.76-.15,2.65-.08.91-.29,1.9-.6,3.02l-32.33.15c.21.66.46,1.3.76,1.89.95,1.92,2.32,3.39,4.09,4.42,1.78,1.03,3.87,1.53,6.28,1.53,2.17,0,4.16-.39,5.95-1.16,1.8-.79,3.37-1.94,4.73-3.49l7.9,7.81c-2.23,2.67-4.92,4.63-8.1,5.91-3.16,1.26-6.66,1.9-10.5,1.9h-.02c-4.9,0-9.24-1.01-13.02-3.02-3.78-2-6.76-4.82-8.93-8.41-2.17-3.6-3.27-7.69-3.27-12.28s1.06-8.47,3.16-12.05c2.11-3.55,5-6.37,8.66-8.41,3.66-2.05,7.77-3.08,12.38-3.08ZM216.57,105.59h-.02s0,0,.01,0c0,0,0,0,0,0ZM401.21,58.53c3.04,0,5.79.6,8.23,1.76,1.59.77,3,1.73,4.22,2.89v-3.71h14.04v45.2h-14.04v-3.68c-1.19,1.12-2.57,2.06-4.14,2.82-2.46,1.18-5.23,1.76-8.33,1.76v.04c-4.09,0-7.77-1.03-11.06-3.12-3.29-2.07-5.87-4.86-7.77-8.37-1.9-3.49-2.83-7.52-2.83-12.05s.95-8.58,2.83-12.13c1.9-3.55,4.47-6.34,7.73-8.37,3.27-2,6.97-3.02,11.12-3.02ZM216.57,59.56c3.35,0,6.35.7,8.97,2.09,2.29,1.21,4.21,2.84,5.73,4.86v-6.02h8.45v44.19h-8.45v-6c-1.5,2.01-3.38,3.63-5.69,4.82-2.66,1.38-5.68,2.08-9.02,2.09-4.09,0-7.77-1.01-11.01-3.02-3.27-2-5.83-4.75-7.73-8.22-1.9-3.47-2.83-7.38-2.83-11.72s.95-8.25,2.83-11.72c1.9-3.47,4.47-6.24,7.73-8.27,3.27-2.05,6.93-3.08,11.02-3.08ZM299.85,59.56c3.1,0,6.12.68,8.66,2.05,2.54,1.36,4.57,3.29,6.1,5.77,1.51,2.48,2.27,5.46,2.27,8.93v28.38h-8.45l-.04-.02v-26.7c0-3.29-1.01-5.87-3.02-7.77-2.03-1.9-4.51-2.83-7.48-2.83-2.05,0-3.89.43-5.54,1.3-1.63.87-2.96,2.09-3.95,3.62-.99,1.55-1.49,3.45-1.49,5.68v26.7h-8.37v-26.7c0-3.29-1.01-5.87-3.02-7.77-2.01-1.9-4.55-2.83-7.59-2.83-2.05,0-3.89.43-5.5,1.3-1.61.87-2.92,2.09-3.91,3.62-.99,1.55-1.49,3.45-1.49,5.68v26.7h-8.37v-44.19h8.37v5.17c1.29-1.63,2.87-2.97,4.75-4,2.54-1.38,5.4-2.09,8.56-2.09s5.91.68,8.41,2.05c2.5,1.36,4.48,3.29,5.95,5.77.04.06.07.13.11.19.03-.05.07-.1.1-.15,1.63-2.44,3.76-4.38,6.37-5.77,2.6-1.38,5.46-2.09,8.56-2.09ZM143.27,90.31l19.75-50.19h9.41l-26.05,64.54h-6.31l-25.96-64.54h9.59l19.57,50.19ZM187.54,104.66h-8.13V40.22h8.13v64.44ZM463.52,36.02c3.1,0,5.81.54,8.14,1.59,2.32,1.05,4.36,2.46,6.1,4.17l-9.03,9.11c-.56-.62-1.2-1.1-1.96-1.45-.74-.33-1.63-.52-2.69-.52-1.98,0-3.51.56-4.61,1.67-1.1,1.12-1.63,2.65-1.63,4.57v6.69c10.07,1.01,16.53.9,22.49-6.36-.02.05-2.81,7.16-8.04,10.83-4.12,2.89-7.7,3.79-14.45,3.53v34.78h-14.33v-35.63c-6.29.07-10.26,1.44-12.67,5.02,0-3.98,4.51-10.21,12.67-12.27v-6.13c0-3.72.81-7.05,2.42-10,1.61-2.93,3.95-5.29,6.99-7.03,3.04-1.74,6.57-2.6,10.61-2.6ZM217.89,67.46c-2.79,0-5.25.64-7.4,1.94-2.15,1.3-3.8,3.08-5.02,5.35-1.22,2.27-1.82,4.86-1.82,7.77,0,2.91.6,5.6,1.82,7.85,1.2,2.27,2.89,4.05,5.06,5.35,2.17,1.3,4.65,1.94,7.44,1.94v.02c4.15,0,7.5-1.41,10.05-4.24,2.54-2.81,3.82-6.43,3.82-10.83,0-2.98-.6-5.6-1.78-7.92-1.18-2.29-2.81-4.07-4.88-5.35-2.09-1.26-4.51-1.9-7.3-1.9ZM404.17,71.49c-1.98,0-3.64.45-5.17,1.34-1.53.91-2.73,2.15-3.64,3.76-.89,1.61-1.34,3.45-1.34,5.48s.45,3.89,1.34,5.5,2.11,2.87,3.64,3.76c1.51.91,3.25,1.34,5.17,1.34,3.04,0,5.48-.99,7.3-2.98,1.82-1.98,2.75-4.57,2.75-7.71,0-2.05-.41-3.86-1.2-5.44-.81-1.59-1.98-2.81-3.54-3.72-1.55-.89-3.33-1.34-5.31-1.34ZM499.74,69.32c-2.23,0-4.18.54-5.81,1.59-1.65,1.05-2.92,2.5-3.82,4.36-.26.55-.48,1.13-.67,1.74l19.58-.11c-.19-.81-.43-1.55-.72-2.23-.74-1.69-1.84-3.02-3.31-3.95-1.47-.93-3.2-1.41-5.25-1.41Z"
      />
    </svg>
  );

  const lightModeFullLogo = (
    <img
      alt="VlamSafe"
      src={fullLogoSrc}
      width="100%"
      height="100%"
      style={{ display: 'block', objectFit: 'contain' }}
    />
  );

  return (
    <LogoRoot
      component={RouterLink}
      href={href}
      aria-label="VlamSafe"
      underline="none"
      className={mergeClasses([logoClasses.root, className])}
      sx={[
        {
          width: 30,
          height: 40,
          ...(!isSingle && { width: 146, height: 35 }),
          ...(disabled && { pointerEvents: 'none' }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {isSingle ? singleLogo : isDarkMode ? darkModeFullLogo : lightModeFullLogo}
    </LogoRoot>
  );
}

// ----------------------------------------------------------------------

const LogoRoot = styled(Link)(() => ({
  flexShrink: 0,
  color: 'transparent',
  display: 'inline-flex',
  verticalAlign: 'middle',
}));
